import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Upload, X, Plus, Trash2 } from 'lucide-react'
import { productService, categoryService, simpleAttributesService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

const ProductForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isEditing = Boolean(id)

  const [form, setForm] = useState({
    name: '',
    description: '',
    short_description: '',
    price: '',
    sale_price: '',
    stock_quantity: '',
    category_id: '',
    sku: '',
    featured: false,
    status: 'published',
    images: [],
    attribute_variations: []
  })

  const [imagePreviews, setImagePreviews] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [selectedAttribute, setSelectedAttribute] = useState('')
  const [selectedVariation, setSelectedVariation] = useState('')
  const [variationPrice, setVariationPrice] = useState('')
  const [availableVariations, setAvailableVariations] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Fetch product data if editing
  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProduct(id),
    enabled: isEditing,
  })

  // Fetch product attributes if editing
  const { data: productAttributes, isLoading: attributesLoading, error: attributesError } = useQuery({
    queryKey: ['productAttributes', id],
    queryFn: () => productService.getProductAttributes(id),
    enabled: !!id,
  })

  // Debug log
  useEffect(() => {
    console.log('ProductForm debug:', { id, isEditing, productAttributes, attributesLoading, attributesError })
  }, [id, isEditing, productAttributes, attributesLoading, attributesError])

  // Update form when product data is loaded
  useEffect(() => {
    if (product && isEditing) {
      setForm({
        name: product.name || '',
        description: product.description || '',
        short_description: product.short_description || '',
        price: product.price || '',
        sale_price: product.sale_price || '',
        stock_quantity: product.stock_quantity || '',
        category_id: product.category_id || '',
        sku: product.sku || '',
        featured: product.featured === 1,
        status: product.status || 'published',
        images: [],
        attribute_variations: []
      })
      
      // Handle existing images
      if (product.images && product.images.length > 0) {
        setExistingImages(product.images.map(img => ({
          id: img.id,
          filename: img.filename,
          sort_order: img.sort_order,
          url: `/uploads/products/${img.filename}`
        })))
      }
    }
  }, [product, isEditing])

  // Update attribute_variations when productAttributes data is loaded
  useEffect(() => {
    if (productAttributes && productAttributes.length > 0) {
      const variations = []
      productAttributes.forEach(attribute => {
        attribute.variations?.forEach(variation => {
          variations.push({
            id: variation.id,
            attribute_id: attribute.id,
            attribute_name: attribute.name,
            variation_title: variation.title,
            price: variation.additional_price || 0
          })
        })
      })
      
      setForm(prev => ({
        ...prev,
        attribute_variations: variations
      }))
    }
  }, [productAttributes])

  const { data: categories } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => categoryService.getCategories(true)
  })

  const { data: simpleAttributes } = useQuery({
    queryKey: ['simple-attributes'],
    queryFn: () => simpleAttributesService.getAttributes()
  })

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      const newImages = []
      const newPreviews = []
      
      files.forEach((file, index) => {
        newImages.push(file)
        
        const reader = new FileReader()
        reader.onload = (e) => {
          newPreviews.push({
            id: `new-${Date.now()}-${index}`,
            url: e.target.result,
            file: file,
            isNew: true
          })
          
          if (newPreviews.length === files.length) {
            setForm(prev => ({ ...prev, images: [...prev.images, ...newImages] }))
            setImagePreviews(prev => [...prev, ...newPreviews])
          }
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const removeImage = async (imageId, isExisting = false) => {
    if (isExisting && id) {
      try {
        // Call API to delete the image from the backend
        const response = await fetch(`/api/products/${id}/images/${imageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        if (response.ok) {
          setExistingImages(prev => prev.filter(img => img.id !== imageId))
          // Show success message
          setMessage('Image deleted successfully')
          setTimeout(() => setMessage(''), 3000)
        } else {
          throw new Error('Failed to delete image')
        }
      } catch (error) {
        console.error('Error deleting image:', error)
        setMessage('Failed to delete image')
        setTimeout(() => setMessage(''), 3000)
      }
    } else {
      // For new images that haven't been saved yet
      setImagePreviews(prev => prev.filter(img => img.id !== imageId))
      setForm(prev => ({
        ...prev,
        images: prev.images.filter((_, index) => `new-${Date.now()}-${index}` !== imageId)
      }))
    }
  }

  const moveImage = (dragIndex, hoverIndex, isExisting = false) => {
    if (isExisting) {
      setExistingImages(prev => {
        const draggedImage = prev[dragIndex]
        const newImages = [...prev]
        newImages.splice(dragIndex, 1)
        newImages.splice(hoverIndex, 0, draggedImage)
        return newImages.map((img, index) => ({ ...img, sort_order: index + 1 }))
      })
    } else {
      setImagePreviews(prev => {
        const draggedImage = prev[dragIndex]
        const newImages = [...prev]
        newImages.splice(dragIndex, 1)
        newImages.splice(hoverIndex, 0, draggedImage)
        return newImages
      })
    }
  }

  const handleAttributeChange = async (attributeId) => {
    setSelectedAttribute(attributeId)
    setSelectedVariation('')
    
    if (attributeId) {
      try {
        const variations = await simpleAttributesService.getAttributeVariations(attributeId)
        setAvailableVariations(variations)
      } catch (error) {
        console.error('Error fetching variations:', error)
        setAvailableVariations([])
      }
    } else {
      setAvailableVariations([])
    }
  }

  const addAttributeVariation = () => {
    if (selectedAttribute && selectedVariation && variationPrice) {
      const attributeName = simpleAttributes?.find(a => a.id === parseInt(selectedAttribute))?.name
      const variationTitle = availableVariations?.find(v => v.id === parseInt(selectedVariation))?.title
      
      const newVariation = {
        attribute_id: parseInt(selectedAttribute),
        variation_id: parseInt(selectedVariation),
        price: parseFloat(variationPrice),
        attribute_name: attributeName,
        variation_title: variationTitle
      }
      
      setForm(prev => ({
        ...prev,
        attribute_variations: [...prev.attribute_variations, newVariation]
      }))
      
      setSelectedAttribute('')
      setSelectedVariation('')
      setVariationPrice('')
      setAvailableVariations([])
    }
  }

  const removeAttributeVariation = (index) => {
    setForm(prev => ({
      ...prev,
      attribute_variations: prev.attribute_variations.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const formData = new FormData()
      Object.keys(form).forEach(key => {
        if (key === 'images') {
          // Add new images
          form.images.forEach((image, index) => {
            formData.append('images', image)
          })
        } else if (key !== 'attribute_variations') {
          formData.append(key, form[key])
        }
      })

      // Add existing images data for updates
      if (isEditing && existingImages.length > 0) {
        formData.append('existingImages', JSON.stringify(existingImages))
      }

      let productId = id
      
      if (isEditing) {
        await productService.updateProduct(id, formData)
        toast.success('Product updated successfully')
      } else {
        const result = await productService.createProduct(formData)
        productId = result.product?.id
        toast.success('Product created successfully')
      }

      // Save product attributes if there are any
      if (form.attribute_variations && form.attribute_variations.length > 0 && productId) {
        const attributeData = {
          attributes: form.attribute_variations.map(attr => ({
            attribute_variation_id: attr.id,
            price: parseFloat(attr.price) || 0,
            is_required: false,
            sort_order: 0
          }))
        }
        
        await productService.updateProductAttributes(productId, attributeData)
      }

      queryClient.invalidateQueries('admin-products')
      queryClient.invalidateQueries(['productAttributes', productId])
      navigate('/admin?tab=products')
    } catch (error) {
      console.error('Error saving product:', error)
      setError(error.message || 'Failed to save product')
    } finally {
      setLoading(false)
    }
  }

  if (productLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin?tab=products')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          العودة إلى المنتجات
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'تعديل المنتج' : 'إنشاء منتج جديد'}
        </h1>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم المنتج *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الوصف المختصر
                </label>
                <textarea
                  value={form.short_description}
                  onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الوصف التفصيلي
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    السعر الأساسي *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    سعر الخصم
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.sale_price}
                    onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Details & Image */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الفئة *
                </label>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">اختر الفئة</option>
                  {categories?.flat_categories?.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رمز المنتج
                  </label>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الكمية المتوفرة *
                  </label>
                  <input
                    type="number"
                    value={form.stock_quantity}
                    onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحالة
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="published">منشور</option>
                  <option value="draft">مسودة</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="h-4 w-4 text-primary focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                  منتج مميز
                </label>
              </div>

              {/* Product Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  صور المنتج
                  <span className="text-xs text-gray-500 ml-2">(الصورة الأولى ستكون الصورة المميزة في البطاقات)</span>
                </label>
                
                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">الصور الحالية</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {existingImages.map((image, index) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.url}
                            alt={`Product image ${index + 1}`}
                            className={`w-full h-32 object-cover rounded-lg border-2 ${
                              index === 0 ? 'border-green-500' : 'border-gray-300'
                            }`}
                          />
                          {index === 0 && (
                            <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-2 py-1 rounded flex items-center">
                              ⭐ مميزة
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(image.id, true)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}                {/* New Images Preview */}
                {imagePreviews.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">الصور الجديدة</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {imagePreviews.map((image, index) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.url}
                            alt={`New image ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-blue-300"
                          />
                          {existingImages.length === 0 && index === 0 && (
                            <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-2 py-1 rounded flex items-center">
                              ⭐ ستكون مميزة
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(image.id, false)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="images" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        رفع الصور
                      </span>
                      <span className="text-xs text-gray-500">اختر عدة ملفات</span>
                      <input
                        id="images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Variations */}
          {simpleAttributes && simpleAttributes.length > 0 && (
            <div className="border-t pt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-6">اختلافات المنتج</h3>
              
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الخاصية
                  </label>
                  <select
                    value={selectedAttribute}
                    onChange={(e) => handleAttributeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">اختر الخاصية</option>
                    {simpleAttributes.map((attr) => (
                      <option key={attr.id} value={attr.id}>
                        {attr.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    التنويع
                  </label>
                  <select
                    value={selectedVariation}
                    onChange={(e) => setSelectedVariation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!selectedAttribute}
                  >
                    <option value="">اختر التنويع</option>
                    {availableVariations.map((variation) => (
                      <option key={variation.id} value={variation.id}>
                        {variation.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    السعر
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={variationPrice}
                    onChange={(e) => setVariationPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={addAttributeVariation}
                    disabled={!selectedAttribute || !selectedVariation || !variationPrice}
                    className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {form.attribute_variations.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">التنويعات الحالية</h4>
                  {form.attribute_variations.map((variation, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <div>
                        <span className="font-medium">{variation.attribute_name}:</span>{' '}
                        <span>{variation.variation_title}</span>{' '}
                        <span className="text-green-600 font-medium">${variation.price}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttributeVariation(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin?tab=products')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-secondary text-white rounded-md hover:bg-secondary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'جاري الحفظ...' : (isEditing ? 'تحديث المنتج' : 'إنشاء المنتج')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProductForm
